/**
 * RoutePhotosGallery Component
 * Displays crowdsourced turn-by-turn navigation photos for a facility
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardMedia,
  CardActions,
  Skeleton,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ZiaCompassIcon from './icons/ZiaCompassIcon';
import CloseIcon from '@mui/icons-material/Close';
import NavigationIcon from '@mui/icons-material/Navigation';
import { routePhotoService, RoutePhoto, LANDMARK_TYPES } from '@services/routePhotoService';
import { telegram } from '@services/telegram';
import { requestPreciseLocation } from '@services/highPrecisionLocation';
import { useAutoDismiss } from '../hooks/useAutoDismiss';

interface RoutePhotosGalleryProps {
  facilityId: string;
  facilityType?: string;
  facilityLat?: number;
  facilityLng?: number;
  compact?: boolean;
}

export function RoutePhotosGallery({
  facilityId,
  facilityType = 'well',
  facilityLat,
  facilityLng,
  compact = false
}: RoutePhotosGalleryProps) {
  const [photos, setPhotos] = useState<RoutePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState<RoutePhoto | null>(null);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useAutoDismiss(error, () => setError(null));
  useAutoDismiss(success, () => setSuccess(null), 3000);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [sequenceNumber, setSequenceNumber] = useState(1);
  const [description, setDescription] = useState('');
  const [landmarkType, setLandmarkType] = useState('');
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);

  const currentUser = telegram.getUser();
  const currentUserId = currentUser?.id?.toString();

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    const fetchedPhotos = await routePhotoService.getRoutePhotos(facilityId, facilityType);
    setPhotos(fetchedPhotos);
    setLoading(false);
  }, [facilityId, facilityType]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    // Get next sequence number
    const nextSeq = await routePhotoService.getNextSequenceNumber(facilityId, facilityType);
    setSequenceNumber(nextSeq);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    telegram.hapticFeedback('medium');

    // Get current device location
    let latitude: number | undefined = facilityLat;
    let longitude: number | undefined = facilityLng;

    try {
      const result = await requestPreciseLocation({
        desiredAccuracyMeters: 15,
        refinementTimeoutMs: 6000,
        allowLastKnownFallback: true,
      });

      if (result.fix) {
        latitude = result.fix.lat;
        longitude = result.fix.lng;
      } else {
        console.warn('Precise location unavailable for route photo:', result.reason);
      }
    } catch (error) {
      console.error('Precise location error for route photo upload:', error);
    }

    const result = await routePhotoService.uploadRoutePhoto({
      file: uploadFile,
      facilityId,
      facilityType,
      sequenceNumber,
      description: description.trim() || undefined,
      landmarkType: landmarkType || undefined,
      latitude,
      longitude,
      facilityLat,
      facilityLng
    });

    setUploading(false);

    if (result.success) {
      setSuccess('Route photo uploaded!');
      setUploadDialogOpen(false);
      resetUploadForm();
      loadPhotos();
    } else {
      setError(result.error || 'Upload failed');
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setDescription('');
    setLandmarkType('');
    setSequenceNumber(1);
  };

  const handleVote = async (photoId: string, voteType: 'up' | 'down') => {
    if (voting) return;
    setVoting(true);
    telegram.hapticFeedback('light');

    const photo = photos.find(p => p.id === photoId);
    if (!photo) {
      setVoting(false);
      return;
    }

    let newVoteType: 'up' | 'down' | 'none' = voteType;
    if (voteType === 'up' && photo.userUpvoted) newVoteType = 'none';
    if (voteType === 'down' && photo.userDownvoted) newVoteType = 'none';

    const result = await routePhotoService.vote(photoId, newVoteType);
    
    if (result) {
      setPhotos(prev => prev.map(p => 
        p.id === photoId 
          ? {
              ...p,
              upvotes: result.upvotes,
              downvotes: result.downvotes,
              score: result.score,
              userUpvoted: result.userVote === 'up',
              userDownvoted: result.userVote === 'down'
            }
          : p
      ));
    }

    setVoting(false);
  };

  const handleDelete = async (photoId: string) => {
    if (!confirm('Delete this route photo?')) return;

    const success = await routePhotoService.deleteRoutePhoto(photoId);
    if (success) {
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSuccess('Photo deleted');
      setFullscreenPhoto(null);
    } else {
      setError('Failed to delete photo');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mb: 1 }} />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // No photos state
  if (photos.length === 0 && !compact) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            borderRadius: 2,
            border: '2px dashed rgba(251, 146, 60, 0.3)',
            backgroundColor: 'rgba(251, 146, 60, 0.05)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(251, 146, 60, 0.6)',
              backgroundColor: 'rgba(251, 146, 60, 0.1)'
            }
          }}
        >
          <ZiaCompassIcon sx={{ fontSize: 40, color: '#fb923c', mb: 1 }} />
          <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center' }}>
            No route photos yet
          </Typography>
          <Typography variant="caption" sx={{ color: '#fb923c', mt: 0.5 }}>
            Help others find this location - add turnoff photos!
          </Typography>
        </Box>

        {/* Upload Dialog */}
        {renderUploadDialog()}
      </Box>
    );
  }

  function renderUploadDialog() {
    return (
      <Dialog
        open={uploadDialogOpen}
        onClose={() => { setUploadDialogOpen(false); resetUploadForm(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            border: '1px solid rgba(251, 146, 60, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <NavigationIcon sx={{ color: '#fb923c' }} />
          Add Route Photo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            Upload a photo of a turnoff, landmark, or navigation point to help others find this location.
          </Typography>

          {!uploadPreview ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <Typography sx={{ color: '#cbd5e1', fontSize: '0.95rem' }}>
                Take a new photo or pick one from your gallery.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PhotoCameraIcon />}
                  sx={{ backgroundColor: '#fb923c', '&:hover': { backgroundColor: '#f97316' } }}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  Take photo
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ borderColor: '#fb923c', color: '#fb923c', '&:hover': { borderColor: '#f97316', color: '#f97316' } }}
                  onClick={() => galleryInputRef.current?.click()}
                >
                  Choose photo
                </Button>
              </Box>
              {/* Hidden inputs to separate camera vs gallery for Android/iOS */}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                ref={cameraInputRef}
                onChange={handleFileSelect}
              />
              <input
                type="file"
                accept="image/*"
                hidden
                ref={galleryInputRef}
                onChange={handleFileSelect}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <img
                src={uploadPreview}
                alt="Preview"
                style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }}
              />
              <Button
                size="small"
                onClick={resetUploadForm}
                sx={{ mt: 1, color: '#94a3b8' }}
              >
                Change Photo
              </Button>
            </Box>
          )}

          <TextField
            label="Step Number"
            type="number"
            value={sequenceNumber}
            onChange={(e) => setSequenceNumber(parseInt(e.target.value) || 1)}
            fullWidth
            sx={{ mt: 2, '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputLabel-root': { color: '#94a3b8' } }}
            InputProps={{ inputProps: { min: 1, max: 20 } }}
            helperText="Order of this photo in the route (1 = first turnoff)"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: '#94a3b8' }}>Landmark Type</InputLabel>
            <Select
              value={landmarkType}
              label="Landmark Type"
              onChange={(e) => setLandmarkType(e.target.value)}
              sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
            >
              <MenuItem value="">None</MenuItem>
              {LANDMARK_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            placeholder="e.g., Turn left at the cattle guard"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2, '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputLabel-root': { color: '#94a3b8' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setUploadDialogOpen(false); resetUploadForm(); }} sx={{ color: '#94a3b8' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
            sx={{ backgroundColor: '#fb923c', '&:hover': { backgroundColor: '#f97316' } }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box sx={{ p: compact ? 1 : 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#fb923c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ZiaCompassIcon fontSize="small" />
          Route Photos ({photos.length})
        </Typography>
        <IconButton
          size="small"
          onClick={() => setUploadDialogOpen(true)}
          sx={{ color: '#fb923c' }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Photo Steps */}
      <Stepper orientation="vertical" sx={{ 
        '& .MuiStepLabel-label': { color: 'white' },
        '& .MuiStepIcon-root': { color: '#fb923c' },
        '& .MuiStepIcon-text': { fill: '#1e293b' },
        '& .MuiStepConnector-line': { borderColor: 'rgba(251, 146, 60, 0.3)' }
      }}>
        {photos.map((photo) => (
          <Step key={photo.id} active expanded>
            <StepLabel
              optional={
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {photo.distance_from_facility 
                    ? routePhotoService.formatDistance(photo.distance_from_facility) + ' from site'
                    : ''}
                </Typography>
              }
            >
              {photo.description || `Step ${photo.sequence_number}`}
              {photo.landmark_type && (
                <Chip
                  label={routePhotoService.getLandmarkLabel(photo.landmark_type)}
                  size="small"
                  sx={{ ml: 1, fontSize: '0.65rem', height: 18, bgcolor: 'rgba(251, 146, 60, 0.2)', color: '#fb923c' }}
                />
              )}
            </StepLabel>
            <StepContent>
              <Card 
                sx={{ 
                  backgroundColor: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(251, 146, 60, 0.2)',
                  cursor: 'pointer'
                }}
                onClick={() => setFullscreenPhoto(photo)}
              >
                <CardMedia
                  component="img"
                  height={compact ? 80 : 120}
                  image={photo.thumbnailUrl}
                  alt={photo.description || `Route photo ${photo.sequence_number}`}
                  sx={{ objectFit: 'cover' }}
                />
                <CardActions sx={{ justifyContent: 'space-between', py: 0.5, px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleVote(photo.id, 'up'); }}
                      disabled={voting}
                      sx={{ color: photo.userUpvoted ? '#22c55e' : '#64748b', p: 0.5 }}
                    >
                      {photo.userUpvoted ? <ThumbUpIcon sx={{ fontSize: 16 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                    <Typography variant="caption" sx={{ 
                      color: photo.score > 0 ? '#22c55e' : photo.score < 0 ? '#ef4444' : '#64748b',
                      minWidth: 16,
                      textAlign: 'center'
                    }}>
                      {photo.score}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleVote(photo.id, 'down'); }}
                      disabled={voting}
                      sx={{ color: photo.userDownvoted ? '#ef4444' : '#64748b', p: 0.5 }}
                    >
                      {photo.userDownvoted ? <ThumbDownIcon sx={{ fontSize: 16 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Box>
                  {photo.telegram_username && (
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      @{photo.telegram_username}
                    </Typography>
                  )}
                </CardActions>
              </Card>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Fullscreen Dialog */}
      <Dialog
        open={!!fullscreenPhoto}
        onClose={() => setFullscreenPhoto(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: 'rgba(15, 23, 42, 0.98)', borderRadius: 2 }
        }}
      >
        {fullscreenPhoto && (
          <>
            <DialogTitle sx={{ 
              color: 'white', 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                Step {fullscreenPhoto.sequence_number}: {fullscreenPhoto.description || 'Route Photo'}
                {fullscreenPhoto.landmark_type && (
                  <Chip
                    label={routePhotoService.getLandmarkLabel(fullscreenPhoto.landmark_type)}
                    size="small"
                    sx={{ ml: 1, bgcolor: 'rgba(251, 146, 60, 0.2)', color: '#fb923c' }}
                  />
                )}
              </Box>
              <IconButton onClick={() => setFullscreenPhoto(null)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <img
                src={fullscreenPhoto.fullUrl}
                alt={fullscreenPhoto.description || 'Route photo'}
                style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              {fullscreenPhoto.photo_latitude && fullscreenPhoto.photo_longitude && (
                <Box sx={{ p: 2, borderTop: '1px solid rgba(251, 146, 60, 0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`📍 ${fullscreenPhoto.photo_latitude.toFixed(5)}, ${fullscreenPhoto.photo_longitude.toFixed(5)}`}
                    size="small"
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${fullscreenPhoto.photo_latitude},${fullscreenPhoto.photo_longitude}`, '_blank')}
                    sx={{
                      backgroundColor: 'rgba(251, 191, 36, 0.15)',
                      color: '#fbbf24',
                      cursor: 'pointer',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
                    }}
                  />
                  {fullscreenPhoto.distance_from_facility && (
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {routePhotoService.formatDistance(fullscreenPhoto.distance_from_facility)} from facility
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() => handleVote(fullscreenPhoto.id, 'up')}
                  sx={{ color: fullscreenPhoto.userUpvoted ? '#22c55e' : '#64748b' }}
                >
                  {fullscreenPhoto.userUpvoted ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                </IconButton>
                <Typography sx={{ color: fullscreenPhoto.score > 0 ? '#22c55e' : '#64748b' }}>
                  {fullscreenPhoto.score}
                </Typography>
                <IconButton
                  onClick={() => handleVote(fullscreenPhoto.id, 'down')}
                  sx={{ color: fullscreenPhoto.userDownvoted ? '#ef4444' : '#64748b' }}
                >
                  {fullscreenPhoto.userDownvoted ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
                </IconButton>
              </Box>
              {fullscreenPhoto.telegram_user_id === currentUserId && (
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(fullscreenPhoto.id)}
                >
                  Delete
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Upload Dialog */}
      {renderUploadDialog()}

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
    </Box>
  );
}

export default RoutePhotosGallery;
